<template>
  <div class="introduction-page">
    <div class="introduction-container flex">
      <div class="guide introduction-guide sticky top-0 p-4 border-r ml-4">
        <div class="list">
          <h2>{{ $t("compliance_docs.label") }}</h2>
          <ul class="menu-root">
            <template v-for="(group, g) in GROUPS" :key="g">
              <li v-if="group.title" class="group-title">
                {{ $t(group.title) }}
              </li>
              <ul class="menu-sub">
                <li v-for="section in group.sections" :key="section.key">
                  <a
                    :href="`#${section.key}`"
                    data-scroll=""
                    class="sidebar-link"
                    @click.prevent="scrollTo($event)"
                    >{{ $t(`compliance_docs.${section.key}.title`) }}</a
                  >
                </li>
              </ul>
            </template>
          </ul>
        </div>
      </div>

      <div class="content flex-1 p-4">
        <h1>{{ $t("compliance_docs.label") }}</h1>

        <template v-for="(group, g) in GROUPS" :key="g">
          <p v-if="group.title" class="group-heading">{{ $t(group.title) }}</p>
          <template v-for="section in group.sections" :key="section.key">
            <h2
              :id="section.key"
              tabindex="-1"
              style="outline: currentcolor none medium"
            >
              <a :href="`#${section.key}`" class="headerlink" data-scroll="">
                {{ $t(`compliance_docs.${section.key}.title`) }}</a
              >
            </h2>
            <div :class="{ 'img-row': section.row }">
              <img
                v-for="(image, index) in section.images || []"
                :key="index"
                :src="image"
                :alt="$t(`compliance_docs.${section.key}.title`)"
              />
            </div>
            <template
              v-for="(block, index) in blocks(section.key)"
              :key="`${section.key}-${index}`"
            >
              <ul v-if="block.list">
                <li v-for="(item, i) in block.lines" :key="i">{{ item }}</li>
              </ul>
              <p v-else>{{ block.lines.join(" ") }}</p>
            </template>
            <p v-if="section.note" class="tip">
              {{ $t(`compliance_docs.${section.key}.note`, labels) }}
            </p>
          </template>
        </template>

        <p class="screenshot-note">
          <em>{{ $t("compliance_docs.screenshot_note") }}</em>
        </p>
      </div>
    </div>
    <v-btn
      v-show="showScrollTop"
      icon
      class="scroll-top-btn"
      color="#F44336"
      @click="scrollToTop"
    >
      <v-icon>mdi-arrow-up</v-icon>
    </v-btn>
  </div>
</template>

<script>
import offerImg from "@/assets/leko/compliance/02-offer.png";
import mobileListImg from "@/assets/leko/compliance/16-mobile-list.png";
import draftImg from "@/assets/leko/compliance/03-draft-opened.png";
import appIdentityImg from "@/assets/leko/compliance/mobile-app/app-identity.png";
import appDocumentsImg from "@/assets/leko/compliance/mobile-app/app-documents.png";
import appWeighingImg from "@/assets/leko/compliance/mobile-app/app-weighing.png";
import appHazardsImg from "@/assets/leko/compliance/mobile-app/app-hazards.png";
import confirmImg from "@/assets/leko/compliance/10-confirm-dialog.png";
import praCaseImg from "@/assets/leko/compliance/11-confirmed.png";
import listImg from "@/assets/leko/compliance/14-list-confirmed.png";
import workshopListImg from "@/assets/leko/compliance/20-workshop-list.png";
import workshopChecklistImg from "@/assets/leko/compliance/21-workshop-checklist.png";
import workshopFormImg from "@/assets/leko/compliance/22-workshop-result-form.png";
import appWorkshopListImg from "@/assets/leko/compliance/mobile-app/app-workshop-list.png";
import appWorkshopTaskListImg from "@/assets/leko/compliance/mobile-app/app-workshop-task-list.png";
import appWorkshopTaskImg from "@/assets/leko/compliance/mobile-app/app-workshop-task.png";

// User guide of the Dismantling & Compliance module (Stage 1 acceptance, Stage 2 PRA case and
// Stage 3 workshop depollution on one page), laid out like Introduction.vue. Texts are in compliance_docs.<section>.{title,text},
// plus .note where note: true. A text is split on blank lines into paragraphs; lines starting with
// "• " become a list. Button and section names come from the module's own locale keys
// (LABEL_KEYS), so the guide quotes the screen in every language.
const GROUPS = [
  { title: null, sections: [{ key: "intro" }] },
  {
    title: "compliance_docs.stage1",
    sections: [
      { key: "s1_start", images: [offerImg, mobileListImg, draftImg] },
      { key: "s1_data" },
      { key: "s1_confirm", images: [confirmImg], note: true },
      { key: "s1_after" },
      {
        key: "s1_mobile",
        images: [
          appIdentityImg,
          appDocumentsImg,
          appWeighingImg,
          appHazardsImg,
        ],
        row: true,
      },
    ],
  },
  {
    title: "compliance_docs.stage2",
    sections: [
      { key: "s2_case", images: [praCaseImg], note: true },
      { key: "s2_late_hold" },
      { key: "s2_changes" },
    ],
  },
  {
    title: "compliance_docs.stage3",
    sections: [
      { key: "s3_list", images: [workshopListImg] },
      {
        key: "s3_tasks",
        images: [workshopChecklistImg, workshopFormImg],
        note: true,
      },
      { key: "s3_approve" },
      { key: "s3_changes" },
      {
        key: "s3_mobile",
        images: [
          appWorkshopListImg,
          appWorkshopTaskListImg,
          appWorkshopTaskImg,
        ],
        row: true,
      },
    ],
  },
  { title: null, sections: [{ key: "lists", images: [listImg] }] },
];

// Placeholder name -> existing locale key of the label shown on screen.
const LABEL_KEYS = {
  vehicleEntry: "vehicle_entry.label",
  inventoryTab: "vehicle_entry.vehicles_inventory",
  historyTab: "vehicle_entry.vehicles_history",
  listColumn: "acceptance.list.column",
  start: "acceptance.list.start",
  open: "acceptance.list.open",
  startNow: "acceptance.offer.start_now",
  draft: "acceptance.status.draft",
  saveDraft: "acceptance.page.save_draft",
  confirm: "acceptance.page.confirm",
  confirmAnyway: "acceptance.confirm.submit_anyway",
  saveChanges: "acceptance.page.save_changes",
  validation: "acceptance.validation.title",
  history: "acceptance.history.title",
  present: "acceptance.checklist.answers.present",
  exception: "acceptance.checklist.answers.exception",
  notApplicable: "acceptance.checklist.answers.not_applicable",
  addWeighing: "acceptance.weighings.add",
  correct: "acceptance.corrections.correct",
  depollutionColumn: "acceptance.list.depollution_column",
  deregistrationColumn: "acceptance.list.deregistration_column",
  praTitle: "acceptance.pra.title",
  praOpen: "acceptance.pra.states.open",
  praCrd: "acceptance.pra.states.crd_recorded",
  praRequested: "acceptance.pra.states.deregistration_requested",
  praConfirmed: "acceptance.pra.states.confirmed",
  recordCrd: "acceptance.pra.crd.action",
  recordRequest: "acceptance.pra.request.action",
  recordReceipt: "acceptance.pra.receipt.action",
  rvfu: "acceptance.pra.request.channels.rvfu_yard",
  sta: "acceptance.pra.request.channels.sta_agency",
  other: "acceptance.pra.request.channels.other",
  late: "acceptance.pra.late_chip",
  putOnHold: "acceptance.pra.hold.action",
  releaseHold: "acceptance.pra.hold.release_action",
  workshopNav: "workshop.nav",
  scan: "workshop.home.scan",
  openFilter: "workshop.home.filters.open",
  toApprove: "workshop.home.filters.to_approve",
  pendingExceptionsFilter: "workshop.home.filters.pending_exceptions",
  overdueFilter: "workshop.home.filters.overdue",
  approvedFilter: "workshop.home.filters.approved",
  depollutionTitle: "acceptance.depollution.column",
  openWorkshop: "acceptance.depollution.open_workshop",
  reusableParts: "workshop.task.reusable_parts_removed",
  resultCompleted: "workshop.result.completed",
  resultNotPresent: "workshop.result.not_present",
  resultNotApplicable: "workshop.result.not_applicable",
  resultException: "workshop.result.exception",
  weighed: "workshop.basis.weighed",
  measured: "workshop.basis.measured",
  estimated: "workshop.basis.estimated",
  takePhoto: "workshop.form.take_photo",
  attachFile: "workshop.form.attach_file",
  saveResult: "workshop.form.save",
  acceptException: "workshop.checklist.accept_exception",
  approve: "workshop.checklist.approve",
  redo: "workshop.checklist.redo",
  previous: "workshop.checklist.previous",
  next: "workshop.checklist.next",
  taskList: "workshop.checklist.task_list",
  complianceBox: "acceptance.list.compliance",
};

const BULLET = "• ";

export default {
  name: "ComplianceDocs",
  data() {
    return {
      GROUPS,
      showScrollTop: false,
    };
  },
  computed: {
    labels() {
      return Object.fromEntries(
        Object.entries(LABEL_KEYS).map(([name, key]) => [name, this.$t(key)]),
      );
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    // [{ list, lines }]: one entry per paragraph or per run of "• " lines.
    blocks(key) {
      const text = this.$t(`compliance_docs.${key}.text`, this.labels);
      const result = [];
      text.split("\n").forEach((line) => {
        const list = line.startsWith(BULLET);
        const value = list ? line.slice(BULLET.length) : line;
        const last = result[result.length - 1];
        if (!line.trim()) {
          if (last) last.closed = true;
        } else if (last && !last.closed && last.list === list) {
          last.lines.push(value);
        } else {
          result.push({ list, lines: [value], closed: false });
        }
      });
      return result;
    },
    handleScroll() {
      this.showScrollTop = window.scrollY > 300;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    scrollTo(event) {
      const target = event.target;
      const id = target.getAttribute("href");
      if (!id || id === "#" || !id.startsWith("#")) return;
      document.querySelectorAll(".sidebar-link").forEach((link) => {
        link.classList.remove("font-weight-bold", "text-blue");
      });
      target.classList.add("font-weight-bold", "text-blue");
      const element = document.querySelector(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        if (history.pushState) history.pushState(null, null, id);
      }
    },
  },
};
</script>

<style scoped>
.introduction-container {
  display: flex;
}
.text-blue {
  color: #3da2cd;
}
.guide {
  width: 260px;
  box-sizing: border-box;
  background: #fff;
  overflow-y: auto;
  flex-shrink: 0;
}
.guide ul {
  list-style-type: none;
  margin: 0;
  line-height: 1.5em;
  padding-left: 1em;
}
.guide li {
  margin-top: 0.5em;
}
.scroll-top-btn {
  position: fixed;
  bottom: 45px;
  right: 15px;
  z-index: 9999;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}
h2 {
  color: #333;
}
li {
  margin-bottom: 10px;
}
a {
  text-decoration: none;
  color: #304455;
}
.content {
  position: relative;
  padding: 35px 0;
  max-width: 700px;
  margin: 0 auto;
  padding-left: 50px;
  overflow-y: auto;
}
.content img {
  display: block;
  max-width: 100%;
  max-height: 520px;
  width: auto;
  height: auto;
  border: 1px solid #e0e0e0;
  margin-bottom: 8px;
}
.content .img-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.content .img-row img {
  max-height: 420px;
  border: none;
}
.content h1 {
  margin: 0 0 1em;
}
.content h2 {
  margin: 45px 0 0.8em;
  padding-bottom: 0.7em;
  border-bottom: 1px solid #ddd;
  pointer-events: none;
}
.content h2 a {
  pointer-events: auto;
  color: #273849;
}
.content h2:before {
  content: "";
  display: block;
  margin-top: -96px;
  height: 96px;
  visibility: hidden;
}
.content p,
.content ul {
  line-height: 1.6em;
  margin: 1.2em 0 -1.2em;
  padding-bottom: 1.2em;
  position: relative;
  z-index: 1;
}
.content ul {
  padding-left: 1.5em;
  position: inherit;
}
.content p.tip {
  padding: 12px 24px 12px 30px;
  margin: 2em 0;
  border-left: 4px solid #f66;
  background-color: #f8f8f8;
  border-bottom-right-radius: 2px;
  border-top-right-radius: 2px;
}
.content p.tip:before {
  content: "!";
  position: absolute;
  top: 14px;
  left: -12px;
  color: #fff;
  background-color: #f66;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  text-align: center;
  line-height: 20px;
  font-weight: bold;
  font-size: 14px;
}
.guide .group-title {
  margin-top: 1em;
  font-weight: bold;
  color: #c62828;
}
.content .group-heading {
  margin-top: 2.5em;
  font-size: 1.3em;
  font-weight: bold;
  color: #c62828;
}
.screenshot-note {
  margin-top: 3em !important;
  color: #4f5959;
}
@media screen and (min-width: 960px) {
  .introduction-container {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    margin-top: 56px;
  }
  .introduction-container .content.flex-1 {
    margin: 0 !important;
    flex: 1;
    min-width: 0;
    max-width: none;
    width: auto;
    padding: 35px 24px !important;
    box-sizing: border-box;
  }
  .guide {
    position: sticky;
    top: 140px;
    width: 260px;
    height: calc(100vh - 160px);
    margin-top: 0;
    z-index: 10;
    border-right: 1px solid #ddd;
    padding: 16px;
  }
}
@media screen and (max-width: 959.98px) {
  .introduction-container {
    flex-direction: column;
  }
  .guide {
    position: static !important;
    margin-left: 0 !important;
    width: 100% !important;
    height: auto !important;
    max-height: 38vh;
    overflow-y: auto;
    border-right: none !important;
    border-bottom: 1px solid #ddd;
    padding: 12px 16px !important;
  }
  .guide .list h2 {
    font-size: 1.1rem;
    margin-bottom: 8px;
  }
  .content {
    padding: 16px !important;
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box;
    margin: 0 !important;
  }
}
</style>
